import PropTypes from 'prop-types';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useMatch, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useGetOverdueStatDataQuery } from '../slices/dashboardApiSlice';
import { useGetOrderPrimaryPhaseStatsQuery, useGetStatisticsOrderYearRangeQuery } from '../slices/statisticsApiSlice';
import { clampCompareAnalysisFiltersForPeriod } from '../screens/statistics/filters/lib/statisticsCompareHelpers';
import { buildLineCompareAnchorFromOrderRange } from '../screens/statistics/filters/lib/statisticsComparePresets';
import { syncStatisticsLineCompareFilterFields } from '../screens/statistics/filters/lib/statisticsTrendFilters';
import { isTrendDimensionAxisMode } from '../screens/statistics/filters/lib/statisticsTrendDimension';
import {
	mergeStatisticsOrderDatePatch,
	sanitizeStatisticsFilters,
	applyOrderDateSideEffectsOnApply,
	ORDER_DATE_QUICK_PICK_CONFIRMED,
} from '../screens/statistics/filters/lib/statisticsOrderDatePatch';
import { buildStatisticsQueryParams, createDefaultOrdersListQuery, createStatisticsFilterDefaults } from '../screens/statistics/filters/lib/statisticsFilterParams';
import { pickTrendFields } from '../screens/statistics/charts/board/lib/statisticsPersistedCharts';
import { getStatisticsQuickPickYearsFallback } from '../screens/statistics/filters/lib/statisticsPeriodQuickPickUtils';
import { hasStatisticsUrlFilterParams, mergeStatisticsFilterSearchParams, searchParamsToFilterFields } from '../screens/statistics/filters/lib/statisticsFilterUrl';
import {
	hasActiveTrendConfig,
	hasCompareAnalysisFiltersPending,
	hasTopBarStatFiltersPending,
	mergeTopBarQueryWithDefaultLineCompare,
} from '../screens/statistics/charts/pipeline/lib/summarizePrimaryPhaseStatPending';
import { canAccessStatisticsPage, canAccessStatisticsOverdueData, canUseStatisticsTeamFilters } from '../screens/statistics/common/lib/statisticsAccess';
import { TREND_CHART_TYPE } from '../screens/statistics/charts/trend/lib/trendChartConfig';

export const StatisticsContext = createContext({ products: [] });

/** 仅影响图表现，不进 API query；改这些字段时不同步 URL，减少整页抖动 */
const TREND_DISPLAY_ONLY_KEYS = new Set([
	'trendLineShape',
	'trendBarOrientation',
	'trendMetricSeriesTypes',
	'trendYearSeriesColors',
]);

const isTrendDisplayOnlyPatch = (patch = {}) => {
	const keys = Object.keys(patch);
	return keys.length > 0 && keys.every((key) => TREND_DISPLAY_ONLY_KEYS.has(key));
};

const mergeLineCompareAnchorForApply = (filters = {}, anchorCustomized = false) => {
	if (anchorCustomized) return filters;
	const anchor = buildLineCompareAnchorFromOrderRange(filters.startDate, filters.endDate, filters.trendAnchor);
	if (!anchor) return filters;
	return { ...filters, trendAnchor: anchor };
};

const stripUnauthorizedTeamFilters = (filters, role) => {
	if (canUseStatisticsTeamFilters(role)) return filters;
	return {
		...filters,
		saleUser: [],
		assistantUser: [],
		customerProvince: [],
		customerCity: [],
	};
};

const STATISTICS_FILTER_APPLY_DEBOUNCE_MS = 400;

const StatisticsProvider = ({ children }) => {
	const isStatisticsPage = Boolean(useMatch('/statistics'));
	const [searchParams, setSearchParams] = useSearchParams();
	const lastSyncedParamsRef = useRef('');
	const searchParamsRef = useRef(searchParams);
	const applyDebounceRef = useRef(null);
	const statisticsFiltersRef = useRef(null);
	const trendAnchorCustomizedRef = useRef(false);
	const commitStatisticsFiltersRef = useRef(null);
	const userInfo = useSelector((state) => state.auth.userInfo);
	const [statisticsFilters, setStatisticsFilters] = useState(createStatisticsFilterDefaults);
	const [statisticsFiltersDraft, setStatisticsFiltersDraft] = useState(statisticsFilters);
	const [trendAnchorCustomized, setLineCompareAnchorCustomized] = useState(false);
	const [statisticsIncludeOrders, setStatisticsIncludeOrders] = useState(true);
	const [ordersListQuery, setOrdersListQuery] = useState(createDefaultOrdersListQuery);

	searchParamsRef.current = searchParams;
	statisticsFiltersRef.current = statisticsFilters;
	trendAnchorCustomizedRef.current = trendAnchorCustomized;

	const clearApplyDebounce = useCallback(() => {
		if (applyDebounceRef.current) {
			clearTimeout(applyDebounceRef.current);
			applyDebounceRef.current = null;
		}
	}, []);

	useEffect(() => () => clearApplyDebounce(), [clearApplyDebounce]);

	useEffect(() => {
		setStatisticsFiltersDraft((draft) => {
			if (hasTopBarStatFiltersPending(draft, statisticsFilters)) {
				return clampCompareAnalysisFiltersForPeriod({
					...draft,
					...pickTrendFields(statisticsFilters),
				});
			}
			return statisticsFilters;
		});
	}, [statisticsFilters]);

	// 替换统计过滤器草稿
	const replaceStatisticsFiltersDraft = useCallback((next) => {
		setStatisticsFiltersDraft(clampCompareAnalysisFiltersForPeriod(next));
	}, []);

	// debounce 窗口内 draft 尚未写入 applied（非用户手动「待应用」）
	const hasTopBarStatFiltersPendingValue = useMemo(() => hasTopBarStatFiltersPending(statisticsFiltersDraft, statisticsFilters), [statisticsFiltersDraft, statisticsFilters]);

	const hasCompareAnalysisFiltersPendingValue = useMemo(
		() => hasCompareAnalysisFiltersPending(statisticsFiltersDraft, statisticsFilters),
		[statisticsFiltersDraft, statisticsFilters],
	);

	const isStatisticsFilterApplyPending = hasTopBarStatFiltersPendingValue || hasCompareAnalysisFiltersPendingValue;

	// 将应用的过滤器同步到 URL（searchParams 用 ref，避免 URL 回写再触发 applied 同步）
	const syncAppliedFiltersToUrl = useCallback(
		(nextFilters) => {
			if (!isStatisticsPage) return;
			const currentParams = searchParamsRef.current;
			const nextParams = mergeStatisticsFilterSearchParams(currentParams, nextFilters);
			const current = currentParams.toString();
			const resolved = nextParams.toString();
			if (current === resolved) return;
			lastSyncedParamsRef.current = resolved;
			setSearchParams(nextParams, { replace: true });
		},
		[isStatisticsPage, setSearchParams],
	);

	/** 趋势/对比配置改即生效（与图表编辑器一致） */
	const patchTrendChartFilters = useCallback(
		(patch) => {
			const displayOnly = isTrendDisplayOnlyPatch(patch);
			/** 线型/柱向/配色等：只改 draft+applied，不走 clamp（避免对比段尚未写入时被 clear 掉颜色） */
			if (displayOnly) {
				setStatisticsFiltersDraft((prevDraft) => ({ ...prevDraft, ...patch }));
				setStatisticsFilters((prevApplied) => ({ ...prevApplied, ...patch }));
				return;
			}
			setStatisticsFiltersDraft((prevDraft) => {
				const { filters, resetAnchorCustomized, setAnchorCustomized } = mergeStatisticsOrderDatePatch(prevDraft, patch, {
					anchorCustomized: trendAnchorCustomized,
				});
				if (resetAnchorCustomized) {
					setLineCompareAnchorCustomized(false);
				}
				if (setAnchorCustomized) {
					setLineCompareAnchorCustomized(true);
				}
				const nextDraft = clampCompareAnalysisFiltersForPeriod(filters);

				setStatisticsFilters((prevApplied) => {
					const nextApplied = clampCompareAnalysisFiltersForPeriod(
						sanitizeStatisticsFilters(
							stripUnauthorizedTeamFilters(
								syncStatisticsLineCompareFilterFields({
									...prevApplied,
									...pickTrendFields(nextDraft),
								}),
								userInfo,
							),
						),
					);
					syncAppliedFiltersToUrl(nextApplied);
					return nextApplied;
				});

				return nextDraft;
			});
		},
		[syncAppliedFiltersToUrl, trendAnchorCustomized, userInfo],
	);

	// 将 URL 的过滤器同步到应用的过滤器
	useEffect(() => {
		if (!isStatisticsPage) return;
		const current = searchParams.toString();
		if (current === lastSyncedParamsRef.current) return;
		if (!hasStatisticsUrlFilterParams(searchParams)) {
			lastSyncedParamsRef.current = current;
			return;
		}

		const scopeFromUrl = searchParamsToFilterFields(searchParams);
		const merged = clampCompareAnalysisFiltersForPeriod(
			sanitizeStatisticsFilters(
				stripUnauthorizedTeamFilters(
					syncStatisticsLineCompareFilterFields({
						...createStatisticsFilterDefaults(),
						...scopeFromUrl,
					}),
					userInfo,
				),
			),
		);
		lastSyncedParamsRef.current = current;
		clearApplyDebounce();
		setStatisticsFilters(merged);
		setStatisticsFiltersDraft(merged);
		syncAppliedFiltersToUrl(merged);
	}, [clearApplyDebounce, isStatisticsPage, searchParams, syncAppliedFiltersToUrl, userInfo]);

	const commitStatisticsFilters = useCallback(
		(nextDraft) => {
			const prevApplied = statisticsFiltersRef.current ?? createStatisticsFilterDefaults();
			const anchorCustomized = trendAnchorCustomizedRef.current;

			let prepared = applyOrderDateSideEffectsOnApply(prevApplied, nextDraft, { anchorCustomized });
			prepared = mergeTopBarQueryWithDefaultLineCompare(prepared, prevApplied, { anchorCustomized });
			const merged = mergeLineCompareAnchorForApply(prepared, anchorCustomized);
			const next = clampCompareAnalysisFiltersForPeriod(sanitizeStatisticsFilters(stripUnauthorizedTeamFilters(syncStatisticsLineCompareFilterFields(merged), userInfo)));
			setStatisticsFilters(next);
			replaceStatisticsFiltersDraft(next);
			setOrdersListQuery(createDefaultOrdersListQuery());
			syncAppliedFiltersToUrl(next);
		},
		[replaceStatisticsFiltersDraft, syncAppliedFiltersToUrl, userInfo],
	);

	commitStatisticsFiltersRef.current = commitStatisticsFilters;

	const applyStatisticsFilters = useCallback(
		(nextDraft) => {
			clearApplyDebounce();
			commitStatisticsFilters(nextDraft);
		},
		[clearApplyDebounce, commitStatisticsFilters],
	);

	// 补丁统计过滤器草稿（统计页改完 debounce 自动 apply；选期确认立即 apply）
	const patchStatisticsFiltersDraft = useCallback(
		(patch) => {
			setStatisticsFiltersDraft((prev) => {
				const { filters, resetAnchorCustomized, setAnchorCustomized } = mergeStatisticsOrderDatePatch(prev, patch, {
					anchorCustomized: trendAnchorCustomizedRef.current,
				});
				if (resetAnchorCustomized) {
					setLineCompareAnchorCustomized(false);
				}
				if (setAnchorCustomized) {
					setLineCompareAnchorCustomized(true);
				}
				const next = clampCompareAnalysisFiltersForPeriod(filters);

				if (isStatisticsPage) {
					const immediate = patch[ORDER_DATE_QUICK_PICK_CONFIRMED] === true;
					clearApplyDebounce();
					if (immediate) {
						commitStatisticsFiltersRef.current?.(next);
					} else {
						applyDebounceRef.current = setTimeout(() => {
							applyDebounceRef.current = null;
							commitStatisticsFiltersRef.current?.(next);
						}, STATISTICS_FILTER_APPLY_DEBOUNCE_MS);
					}
				}

				return next;
			});
		},
		[clearApplyDebounce, isStatisticsPage],
	);

	// 重置过滤器
	const resetStatisticsFilters = useCallback(() => {
		clearApplyDebounce();
		const next = createStatisticsFilterDefaults();
		setLineCompareAnchorCustomized(false);
		setStatisticsFilters(next);
		replaceStatisticsFiltersDraft(next);
		setOrdersListQuery(createDefaultOrdersListQuery());
		syncAppliedFiltersToUrl(next);
	}, [clearApplyDebounce, replaceStatisticsFiltersDraft, syncAppliedFiltersToUrl]);

	/** 离开业务统计页：筛选取默认（Provider 在 App 层，否则会带回上次条件） */
	const wasStatisticsPageRef = useRef(isStatisticsPage);
	useEffect(() => {
		const wasOnPage = wasStatisticsPageRef.current;
		wasStatisticsPageRef.current = isStatisticsPage;
		if (!wasOnPage || isStatisticsPage) return;

		clearApplyDebounce();
		const next = createStatisticsFilterDefaults();
		setLineCompareAnchorCustomized(false);
		setStatisticsFilters(next);
		replaceStatisticsFiltersDraft(next);
		setOrdersListQuery(createDefaultOrdersListQuery());
		setStatisticsIncludeOrders(true);
		lastSyncedParamsRef.current = '';
	}, [clearApplyDebounce, isStatisticsPage, replaceStatisticsFiltersDraft]);

	// 超期：统计页全量；首页由超期区/经营看板内的超期图自行按需拉取（进视口再挂载）
	const shouldFetchOverdueData = canAccessStatisticsPage(userInfo) && canAccessStatisticsOverdueData(userInfo) && isStatisticsPage;
	const overdueQueryArg = undefined;

	// 获取逾期数据
	const {
		data: overdueData = {},
		isLoading,
		refetch: refetchOverdueStatistics,
		isUninitialized,
	} = useGetOverdueStatDataQuery(overdueQueryArg, {
		skip: !shouldFetchOverdueData,
	});

	const primaryPhaseFiltersForQuery = statisticsFilters;

	const canQueryStatistics = isStatisticsPage && canAccessStatisticsPage(userInfo);

	/**
	 * 主请求固定第 1 页（含下钻条件）：聚合 + overviewReceivable + 本页订单一锅出。
	 * 翻页（page>1）另走同筛选 ordersOnly，只读本页（默认 7 条，对齐 Falcon），不重算 KPI。
	 */
	const primaryOrdersList = useMemo(
		() => ({
			page: 1,
			limit: ordersListQuery.limit,
			phase: ordersListQuery.phase,
			dimension: ordersListQuery.dimension,
			dimensionKey: ordersListQuery.dimensionKey,
			statusField: ordersListQuery.statusField,
			statusValue: ordersListQuery.statusValue,
			geoProvince: ordersListQuery.geoProvince,
			geoCityCode: ordersListQuery.geoCityCode,
			/** 状态筛选改走 ordersOnly 叠层，避免重算 KPI */
			paymentStatuses: [],
			deliveryStatuses: [],
			fulfillmentComplete: false,
			pendingReview: false,
			inProgress: false,
			verified: false,
		}),
		[
			ordersListQuery.dimension,
			ordersListQuery.dimensionKey,
			ordersListQuery.geoCityCode,
			ordersListQuery.geoProvince,
			ordersListQuery.limit,
			ordersListQuery.phase,
			ordersListQuery.statusField,
			ordersListQuery.statusValue,
		],
	);

	const hasOrdersListStatusFilter = useMemo(() => {
		const paymentLen = Array.isArray(ordersListQuery.paymentStatuses) ? ordersListQuery.paymentStatuses.length : 0;
		const deliveryLen = Array.isArray(ordersListQuery.deliveryStatuses) ? ordersListQuery.deliveryStatuses.length : 0;
		return (
			paymentLen > 0 ||
			deliveryLen > 0 ||
			Boolean(ordersListQuery.fulfillmentComplete) ||
			Boolean(ordersListQuery.pendingReview) ||
			Boolean(ordersListQuery.inProgress) ||
			Boolean(ordersListQuery.verified)
		);
	}, [
		ordersListQuery.deliveryStatuses,
		ordersListQuery.fulfillmentComplete,
		ordersListQuery.inProgress,
		ordersListQuery.paymentStatuses,
		ordersListQuery.pendingReview,
		ordersListQuery.verified,
	]);

	const needsOrdersListOverlay = Number(ordersListQuery.page) > 1 || hasOrdersListStatusFilter;
	/** 维度横轴 / 地图：要按全量订单做聚合；主查询若带 ordersList 分页会只拿到当页单且跳过跨年合并 */
	const needsFullChartOrders =
		isTrendDimensionAxisMode(primaryPhaseFiltersForQuery?.trendAxisMode) ||
		(primaryPhaseFiltersForQuery?.trendChartType ?? TREND_CHART_TYPE.BAR) === TREND_CHART_TYPE.MAP;
	const useOrdersListOverlay = needsOrdersListOverlay || needsFullChartOrders;
	const needsComparesQuery =
		hasActiveTrendConfig(primaryPhaseFiltersForQuery) ||
		primaryPhaseFiltersForQuery?.compareYoy !== false ||
		primaryPhaseFiltersForQuery?.compareMom === true;

	const primaryPhaseQueryParams = useMemo(
		() =>
			buildStatisticsQueryParams(primaryPhaseFiltersForQuery, {
				includeOrders: statisticsIncludeOrders,
				includeOrderIds: false,
				ordersList: statisticsIncludeOrders && !needsFullChartOrders ? primaryOrdersList : null,
				includeCompares: false,
			}),
		[needsFullChartOrders, primaryOrdersList, statisticsIncludeOrders, primaryPhaseFiltersForQuery],
	);

	/** 首屏后：仅同比/环比/锚点/自定义对比（lensesOnly） */
	const comparesQueryParams = useMemo(
		() =>
			buildStatisticsQueryParams(primaryPhaseFiltersForQuery, {
				includeOrders: false,
				includeOrderIds: false,
				comparesOnly: true,
			}),
		[primaryPhaseFiltersForQuery],
	);

	/** 翻页 / 维度横轴时的列表页：同筛选 ordersOnly，只查本页 */
	const ordersListQueryParams = useMemo(
		() =>
			buildStatisticsQueryParams(primaryPhaseFiltersForQuery, {
				includeOrders: true,
				includeOrderIds: false,
				ordersList: needsFullChartOrders
					? { ...ordersListQuery, page: Number(ordersListQuery.page) > 0 ? ordersListQuery.page : 1 }
					: ordersListQuery,
				ordersOnly: true,
			}),
		[needsFullChartOrders, ordersListQuery, primaryPhaseFiltersForQuery],
	);

	const {
		data: primaryPhaseAggregateStats,
		isLoading: isStatisticsQueryLoading,
		isFetching: isStatisticsQueryFetching,
		refetch: refetchPrimaryPhaseAggregate,
	} = useGetOrderPrimaryPhaseStatsQuery(primaryPhaseQueryParams, {
		skip: !canQueryStatistics,
	});

	const {
		data: comparesStats,
		isLoading: isComparesQueryLoading,
		isFetching: isComparesQueryFetching,
	} = useGetOrderPrimaryPhaseStatsQuery(comparesQueryParams, {
		skip: !canQueryStatistics || !needsComparesQuery,
	});

	const {
		data: ordersListStats,
		isLoading: isOrdersListLoading,
		isFetching: isOrdersListFetching,
		refetch: refetchOrdersList,
	} = useGetOrderPrimaryPhaseStatsQuery(ordersListQueryParams, {
		skip: !canQueryStatistics || !useOrdersListOverlay,
	});

	/** 订单收款/履约等写操作后：刷新超期 +（仅统计页）主阶段聚合（含回款日历） */
	const refreshStatisticsAfterOrderChange = useCallback(() => {
		if (shouldFetchOverdueData) {
			refetchOverdueStatistics();
		}
		if (!canQueryStatistics) return;
		refetchPrimaryPhaseAggregate();
		if (useOrdersListOverlay) {
			refetchOrdersList();
		}
	}, [canQueryStatistics, refetchOrdersList, refetchOverdueStatistics, refetchPrimaryPhaseAggregate, shouldFetchOverdueData, useOrdersListOverlay]);

	const ordersListStatsRef = useRef(null);
	if (ordersListStats) {
		ordersListStatsRef.current = ordersListStats;
	}
	if (!useOrdersListOverlay) {
		ordersListStatsRef.current = null;
	}
	const stableOrdersListStats = ordersListStats ?? (isOrdersListFetching ? ordersListStatsRef.current : null);

	const primaryPhaseStats = useMemo(() => {
		if (!primaryPhaseAggregateStats) return primaryPhaseAggregateStats;
		const listOrders = stableOrdersListStats?.orders ?? [];
		const listPagination = stableOrdersListStats?.ordersPagination ?? primaryPhaseAggregateStats.ordersPagination;
		const listCustomerGroup =
			stableOrdersListStats?.filterOrderGroupByCustomer ?? primaryPhaseAggregateStats.filterOrderGroupByCustomer;
		const withOrders = needsFullChartOrders
			? {
					...primaryPhaseAggregateStats,
					/** 图表用全量；列表另走 overviewListOrders，避免分页盖掉同比订单 */
					orders: primaryPhaseAggregateStats.orders ?? [],
					overviewListOrders: useOrdersListOverlay ? listOrders : primaryPhaseAggregateStats.orders ?? [],
					ordersPagination: useOrdersListOverlay ? listPagination : primaryPhaseAggregateStats.ordersPagination,
					filterOrderGroupByCustomer: useOrdersListOverlay
						? listCustomerGroup
						: primaryPhaseAggregateStats.filterOrderGroupByCustomer,
				}
			: !useOrdersListOverlay
				? {
						...primaryPhaseAggregateStats,
						orders: primaryPhaseAggregateStats.orders ?? [],
					}
				: {
						...primaryPhaseAggregateStats,
						orders: listOrders,
						ordersPagination: listPagination,
						filterOrderGroupByCustomer: listCustomerGroup,
					};

		if (!comparesStats || isComparesQueryFetching) {
			return {
				...withOrders,
				compares: [],
				yoy: null,
				mom: null,
			};
		}

		return {
			...withOrders,
			compares: comparesStats.compares ?? [],
			yoy: comparesStats.yoy ?? null,
			mom: comparesStats.mom ?? null,
		};
	}, [
		comparesStats,
		isComparesQueryFetching,
		needsFullChartOrders,
		primaryPhaseAggregateStats,
		stableOrdersListStats,
		useOrdersListOverlay,
	]);

	// 获取订单年份范围
	const { data: orderYearRange } = useGetStatisticsOrderYearRangeQuery(undefined, {
		skip: !isStatisticsPage || !canAccessStatisticsPage(userInfo),
	});

	// 获取订单年份快速选择年份
	const statisticsQuickPickYears = useMemo(() => {
		const raw = orderYearRange?.years?.length ? orderYearRange.years : getStatisticsQuickPickYearsFallback();
		return raw.map((year) => Number(year)).filter((year) => Number.isFinite(year));
	}, [orderYearRange?.years]);

	const contextValue = useMemo(
		() => ({
			overdueData,
			refetchOverdueStatistics,
			refreshStatisticsAfterOrderChange,
			isLoading,
			isUninitialized,
			primaryPhaseStats,
			isStatisticsQueryLoading,
			isStatisticsQueryFetching,
			isComparesQueryLoading,
			isComparesQueryFetching,
			isOrdersListLoading: Boolean(useOrdersListOverlay && isOrdersListLoading),
			isOrdersListFetching: Boolean(useOrdersListOverlay && isOrdersListFetching),
			statisticsFilters,
			setStatisticsFilters,
			statisticsFiltersDraft,
			patchStatisticsFiltersDraft,
			patchTrendChartFilters,
			replaceStatisticsFiltersDraft,
			hasStatisticsFiltersPending: isStatisticsFilterApplyPending,
			isStatisticsFilterApplyPending,
			hasTopBarStatFiltersPending: hasTopBarStatFiltersPendingValue,
			hasCompareAnalysisFiltersPending: hasCompareAnalysisFiltersPendingValue,
			applyStatisticsFilters,
			resetStatisticsFilters,
			syncAppliedFiltersToUrl,
			trendAnchorCustomized,
			setLineCompareAnchorCustomized,
			statisticsIncludeOrders,
			setStatisticsIncludeOrders,
			ordersListQuery,
			setOrdersListQuery,
			statisticsQuickPickYears,
			orderYearRange,
		}),
		[
			applyStatisticsFilters,
			hasCompareAnalysisFiltersPendingValue,
			hasTopBarStatFiltersPendingValue,
			isComparesQueryFetching,
			isComparesQueryLoading,
			isStatisticsFilterApplyPending,
			isLoading,
			isOrdersListFetching,
			isOrdersListLoading,
			isStatisticsQueryFetching,
			isStatisticsQueryLoading,
			isUninitialized,
			trendAnchorCustomized,
			useOrdersListOverlay,
			orderYearRange,
			ordersListQuery,
			overdueData,
			patchStatisticsFiltersDraft,
			patchTrendChartFilters,
			statisticsIncludeOrders,
			statisticsFilters,
			statisticsFiltersDraft,
			primaryPhaseStats,
			refetchOverdueStatistics,
			refreshStatisticsAfterOrderChange,
			replaceStatisticsFiltersDraft,
			resetStatisticsFilters,
			setLineCompareAnchorCustomized,
			setOrdersListQuery,
			setStatisticsFilters,
			setStatisticsIncludeOrders,
			statisticsQuickPickYears,
			syncAppliedFiltersToUrl,
		],
	);

	return <StatisticsContext.Provider value={contextValue}>{children}</StatisticsContext.Provider>;
};

StatisticsProvider.propTypes = {
	children: PropTypes.node.isRequired,
};

export const useStatisticsContext = () => useContext(StatisticsContext);

export default StatisticsProvider;
